import React from "react";
import styled, { css } from "styled-components";
import { colors, radius, shadow, transition, weights } from "./theme";
import { Spinner } from "./Styles";

const Button = ({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  icon,
  iconRight,
  children,
  disabled,
  ...rest
}) => (
  <StyledBtn
    $variant={variant}
    $size={size}
    fullWidth={fullWidth}
    disabled={disabled || loading}
    {...rest}
  >
    {loading ? (
      <>
        <Spinner />
        {children}
      </>
    ) : (
      <>
        {icon}
        {children}
        {iconRight}
      </>
    )}
  </StyledBtn>
);

export default Button;

const variants = {
  primary: css`
    background: #00838c;
    color: #ffffff;
    border: none;
    box-shadow: ${shadow.teal};
    &:hover:not(:disabled) {
      background: linear-gradient(
        135deg,
        ${colors.tealDark} 0%,
        ${colors.teal} 100%
      );
      box-shadow: 0 8px 32px rgba(0, 131, 140, 0.32);
      transform: translateY(-2px);
    }
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: ${shadow.teal};
    }
  `,
  secondary: css`
    background: #ffffff;
    color: #00838c;
    border: 1.5px solid #00838c;
    box-shadow: ${shadow.xs};
    &:hover:not(:disabled) {
      border-color: ${colors.teal};
      color: ${colors.teal};
      box-shadow: ${shadow.sm};
      transform: translateY(-1px);
    }
  `,
  navy: css`
    background: linear-gradient(
      135deg,
      ${colors.navy} 0%,
      ${colors.navyLight} 100%
    );
    color: ${colors.white};
    border: none;
    box-shadow: ${shadow.navy};
    &:hover:not(:disabled) {
      background: linear-gradient(
        135deg,
        ${colors.navyDark} 0%,
        ${colors.navy} 100%
      );
      transform: translateY(-2px);
    }
  `,
  ghost: css`
    background: transparent;
    color: ${colors.teal};
    border: 1.5px solid ${colors.tealFog};
    &:hover:not(:disabled) {
      background: ${colors.tealGhost};
      border-color: ${colors.teal};
    }
  `,
  danger: css`
    background: ${colors.white};
    color: #c0392b;
    border: 1.5px solid #fde0de;
    &:hover:not(:disabled) {
      background: #fef0ef;
      border-color: #c0392b;
    }
  `,
};

const sizes = {
  sm: css`
    padding: 8px 18px;
    font-size: 12px;
    border-radius: ${radius.sm};
  `,
  md: css`
    padding: 11px 26px;
    font-size: 13.5px;
    border-radius: ${radius.md};
  `,
  lg: css`
    padding: 14px 34px;
    font-size: 14.5px;
    border-radius: ${radius.md};
  `,
  xl: css`
    padding: 16px 40px;
    font-size: 15px;
    border-radius: ${radius.lg};
  `,
};

const StyledBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: "Open Sans", sans-serif;
  font-weight: ${weights.semiBold};
  letter-spacing: 0.01em;
  transition: ${transition.base};
  cursor: pointer;
  outline: none;
  position: relative;
  overflow: hidden;
  width: ${(p) => (p.fullWidth ? "100%" : "auto")};
  white-space: nowrap;
  ${(p) => variants[p.$variant] || variants.primary}
  ${(p) => sizes[p.$size] || sizes.md}
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.08);
    opacity: 0;
    transition: opacity 0.2s;
  }
  &:active::after {
    opacity: 1;
  }
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;
